import React, { useState, useEffect } from 'react';
import { FiAlertTriangle, FiX, FiCheck } from 'react-icons/fi';
import './ConfirmationModal.css';

/**
 * Beautiful Confirmation Modal Component
 * 
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Function to call when closing modal
 * @param {function} onConfirm - Function to call when confirming action
 * @param {string} title - Modal title
 * @param {string} message - Main message describing the action
 * @param {string} actionType - Type of action: 'delete', 'reset', 'clear', 'remove'
 * @param {string} confirmText - Text for confirm button (optional)
 * @param {string} itemName - Name of item being acted upon (optional)
 * @param {boolean} requiresConfirmation - Whether to show the confirmation toggle (default: true)
 */
const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  actionType = 'delete',
  confirmText,
  itemName,
  requiresConfirmation = true
}) => {
  const [confirmationChecked, setConfirmationChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setConfirmationChecked(false);
      setIsLoading(false);
      setError(null);
    }
  }, [isOpen, title, actionType, itemName]);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && !isLoading) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, isLoading]);

  const handleConfirm = async () => {
    if (requiresConfirmation && !confirmationChecked) return;
    if (isLoading) return; // Prevent double-clicks
    
    setIsLoading(true);
    setError(null);
    
    try {
      await onConfirm();
      handleClose(); // Only close on success
    } catch (err) {
      setError(err.message || 'An error occurred');
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setConfirmationChecked(false);
    setIsLoading(false);
    setError(null);
    onClose();
  };

  const getActionConfig = () => {
    const configs = {
      delete: {
        icon: FiX,
        color: 'danger',
        defaultConfirmText: 'Delete',
        confirmationText: 'I understand this action cannot be undone'
      },
      reset: {
        icon: FiAlertTriangle,
        color: 'warning',
        defaultConfirmText: 'Reset',
        confirmationText: 'I understand this will clear all historical data'
      },
      clear: {
        icon: FiAlertTriangle,
        color: 'warning',
        defaultConfirmText: 'Clear',
        confirmationText: 'I understand this will remove current data'
      },
      remove: {
        icon: FiX,
        color: 'danger',
        defaultConfirmText: 'Remove',
        confirmationText: 'I understand this action cannot be undone'
      }
    };
    return configs[actionType] || configs.delete;
  };

  const config = getActionConfig();
  const IconComponent = config.icon;

  if (!isOpen) return null;

  return (
    <div 
      className="confirmation-modal-overlay"
      onClick={(e) => {
        // Only close if clicking the overlay, not the modal content
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <div 
        className="confirmation-modal"
        onClick={(e) => {
          // Prevent clicks inside modal from bubbling to overlay
          e.stopPropagation();
        }}
      >
        {/* Header */}
        <div className={`confirmation-modal-header ${config.color}`}>
          <div className="confirmation-modal-icon">
            <IconComponent />
          </div>
          <h2 className="confirmation-modal-title">{title}</h2>
          <button 
            className="confirmation-modal-close"
            onClick={handleClose}
            aria-label="Close modal"
          >
            <FiX />
          </button>
        </div>

        {/* Content */}
        <div className="confirmation-modal-content">
          <div className="confirmation-message">
            {message}
          </div>

          {itemName && (
            <div className="confirmation-item-highlight">
              <strong>{itemName}</strong>
            </div>
          )}

          {/* Confirmation Toggle */}
          {requiresConfirmation && (
            <div className="confirmation-toggle-container">
              <label className="confirmation-toggle">
                <input
                  type="checkbox"
                  checked={confirmationChecked}
                  onChange={(e) => setConfirmationChecked(e.target.checked)}
                  className="confirmation-checkbox"
                  disabled={isLoading}
                />
                <span className="confirmation-checkmark">
                  {confirmationChecked && <FiCheck />}
                </span>
                <span className="confirmation-toggle-text">
                  {config.confirmationText}
                </span>
              </label>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="confirmation-error">
              <FiAlertTriangle />
              <span>{error}</span>
            </div>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="confirmation-loading">
              <div className="confirmation-spinner"></div>
              <span>Processing...</span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="confirmation-modal-actions">
          <button
            className="confirmation-btn confirmation-btn-secondary"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            className={`confirmation-btn confirmation-btn-${config.color}`}
            onClick={handleConfirm}
            disabled={(requiresConfirmation && !confirmationChecked) || isLoading}
          >
            {isLoading ? 'Processing...' : (confirmText || config.defaultConfirmText)}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal; 